import { Lock, LockOpen, NewReleases, Star as StarIcon, VisibilityRounded } from "@mui/icons-material";
import Logger from "../../../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Tooltip,
    Typography
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import AuthService from "../../../../services/AuthService";
import ColorService from "../../../../services/ColorService";
import {
    getJigsawDifficultyDisplayInfo,
    getJigsawDifficultyLabelKey,
    resolveJigsawTemplateTileSize,
} from "../../jigsaw/jigsawBrowseUtils";
import UtilService from "../../../../services/UtilService";
import { isCatalogFreePuzzle, isWordPuzzleOpenAccess } from "../puzzleTierAccess";
import { TagChipAvatar } from "./TagChipAvatar";
import SubscriptionDialog from "../../../subscription/SubscriptionDialog";
import { usePagesTranslation } from "../../../locales";
import ClusteredBubblesMiniChart from "../../../components/ClusteredBubblesMiniChart";
import { isPlayDataComplete, resolvePuzzleCardRawTags } from "../utils/PuzzleCardHelpers";
import { SURFACE } from "../../../../dark-theme/palette";

/** Compact styling for Free / Standard tier badges above the card. */
const tierBadgeChipSx = {
    pointerEvents: "auto",
    flexShrink: 0,
    maxWidth: "none",
    height: 22,
    "& .MuiChip-label": {
        px: 0.65,
        py: 0,
        fontSize: "0.68rem",
        lineHeight: 1.2,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    "& .MuiChip-icon": {
        fontSize: "0.8rem",
        marginLeft: "4px",
        marginRight: "-2px",
    },
};

/** Contained play actions: same green as filter / progress accents (#3BA666). */
const playContainedButtonSx = {
    backgroundColor: "#3BA666",
    color: "#fff",
    "&:hover": {
        backgroundColor: "#2e7d32",
    },
    "&.Mui-disabled": {
        backgroundColor: "action.disabledBackground",
        color: "action.disabled",
    },
};

/**
 * Base PuzzleCard component that can be used by both Grid and Crossword puzzles
 * @param {Object} props
 * @param {Object} props.puzzle - The puzzle object
 * @param {string} props.userPayPlan - User's payment plan ('FREE' or 'STANDARD')
 * @param {string} props.subscriptionStatus - Subscription status
 * @param {Function} props.onPlayClick - Callback when play button is clicked
 * @param {string} props.buttonText - Button text (default: "Play")
 * @param {boolean} props.isInProgress - Whether puzzle is in progress
 * @param {boolean} props.forceUnlock - Force unlock the puzzle
 * @param {Object} props.service - Service object with startPlay and restartPlay methods
 * @param {Function} props.getProgress - Function to calculate progress from puzzle
 * @param {Function} props.getPlayData - Function to get play data (hwPlay or crossPlay)
 * @param {Function} props.getWordCount - Function to calculate word count
 * @param {Function} props.getNavigationPath - Function to get navigation path for puzzle
 * @param {Function} props.getCardContentPadding - Function to get CardContent padding
 * @param {boolean} props.hideWordCountChip - Hide the "N words" chip (e.g. browse layout)
 * @param {React.ReactNode} props.belowDifficultySlot - Rendered under difficulty row, above mini chart
 * @param {boolean} props.browseStoriesChrome - Match /stories browse cards: outlined surface + hover shadow/border (browse puzzles only)
 * @param {boolean} props.hideClusteredBubbles - Omit word-network bubble mini chart (e.g. account library)
 * @param {React.ReactNode} props.primaryActionReplacement - When set (and not locked), replaces Play/Replay button (e.g. library PDF + story icons)
 * @param {number} [props.topBadgesOffsetY=0] - Extra px added to the absolute `top` of New/Free/Standard chips (positive moves down).
 * @param {boolean} [props.hideFreeTierBadge=false] - When true, omit the Free chip but still show Standard for paid puzzles.
 * @param {boolean} [props.hideTierBadges=false] - When true, omit both Free and Standard tier chips (e.g. account library).
 */
const PuzzleCardBase = ({
    puzzle,
    userPayPlan,
    subscriptionStatus,
    onPlayClick,
    buttonText = "Play",
    isInProgress = false,
    forceUnlock = false,
    service,
    getProgress,
    getPlayData,
    getWordCount,
    getNavigationPath,
    getCardContentPadding,
    hideWordCountChip = false,
    belowDifficultySlot = null,
    browseStoriesChrome = false,
    hideClusteredBubbles = false,
    primaryActionReplacement = null,
    stackPrimaryAction = false,
    actionButtonSx = null,
    topBadgesOffsetY = 0,
    hideFreeTierBadge = false,
    hideTierBadges = false,
}) => {
    const { t, lang } = usePagesTranslation();
    const navigate = useNavigate();
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [replayConfirmOpen, setReplayConfirmOpen] = useState(false);
    const puzzleTypeUpper = String(puzzle?.type ?? "").toUpperCase();
    const isWordPuzzleType =
        puzzleTypeUpper === "CROSSWORD"
        || puzzleTypeUpper === "WORD_SEARCH"
        || puzzleTypeUpper === "HIDDEN_MESSAGE";
    /** Catalog `free` flag. Word puzzles also keep Easy open even when the puzzle is premium. */
    const markedFree = isCatalogFreePuzzle(puzzle);
    const wordOpenByDifficulty = isWordPuzzleType && isWordPuzzleOpenAccess(puzzle.difficultyLevel);
    const playableWithoutAccount = isWordPuzzleType
        ? (markedFree || wordOpenByDifficulty)
        : markedFree;
    const isAnonymous = !userPayPlan;

    const resolvedPublishedAt =
        puzzle.publishedAt ??
        puzzle.published_at ??
        puzzle.template?.publishedAt ??
        puzzle.template?.published_at ??
        null;

    // Check if puzzle is disabled
    const isDisabled = puzzle.state === 'DISABLED' || puzzle.disabled === true;

    // Locked for guests when credit-gated (Medium/Hard word puzzles, or non-free other types).
    // A catalog-free puzzle is never locked, including harder difficulties.
    const isLocked = forceUnlock ? false : (
        playableWithoutAccount ? false : isAnonymous
    );

    // NEW = published in the last ~1 day (not future-dated — future dates wrongly satisfied > oneDayAgo for everything)
    const isNewPuzzle = () => {
        if (resolvedPublishedAt == null || resolvedPublishedAt === "") return false;
        const pubMs = new Date(resolvedPublishedAt).getTime();
        if (!Number.isFinite(pubMs)) return false;
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;
        if (pubMs > now) return false;
        return pubMs >= now - oneDayMs;
    };

    const progress = getProgress ? getProgress(puzzle, isInProgress) : null;
    const playData = getPlayData ? getPlayData(puzzle) : null;
    const playComplete = isPlayDataComplete(playData);
    const wordCount = getWordCount ? getWordCount(puzzle) : (puzzle.words?.split('\n').length || 0);

    const showClusteredBubblesMiniChart =
        !hideClusteredBubbles &&
        Boolean(puzzle.template?.clusteredBubbles?.children?.length > 0);

    // Tags from template/tpl/root only (not topic.tags); genre fallback when empty.
    const genreTag =
        puzzle.topic?.genre?.name
        ?? puzzle.template?.topic?.genre?.name
        ?? puzzle.crossword?.crossTpl?.topic?.genre?.name
        ?? puzzle.crossTpl?.topic?.genre?.name
        ?? puzzle.grid?.gridTpl?.topic?.genre?.name
        ?? puzzle.gridTpl?.[0]?.topic?.genre?.name;
    let rawTags = resolvePuzzleCardRawTags(puzzle);
    if (!rawTags.length && genreTag) {
        rawTags = [genreTag];
    }
    const displayTags = (Array.isArray(rawTags) ? rawTags : [])
        .filter((t) => t && String(t).toLowerCase() !== 'bedrock')
        .map((t) => String(t).trim());
    const cardContentPadding = getCardContentPadding 
        ? getCardContentPadding(userPayPlan, isNewPuzzle()) 
        : (!UtilService.isStandardPayPlan(userPayPlan) ? 6 : 2);

    const handlePlayClick = async () => {
        // Don't allow replay if puzzle is disabled
        if (isDisabled && buttonText === "Replay") {
            return;
        }

        if (!isLocked) {
            // Show confirmation dialog for Replay (only for library, not in-progress)
            if (
                buttonText === "Replay" &&
                !isInProgress &&
                primaryActionReplacement == null
            ) {
                setReplayConfirmOpen(true);
                return;
            }

            // Call appropriate API based on button text
            if (userPayPlan && service) {
                try {
                    if (buttonText === "Replay") {
                        await service.restartPlay(puzzle.id);
                        Logger.log('Puzzle play restarted successfully');
                    } else {
                        await service.startPlay(puzzle.id);
                        Logger.log('Puzzle play started successfully');
                    }
                } catch (error) {
                    Logger.error('Error with puzzle play:', error);
                }
            }

            // If onPlayClick is provided, use it (for library navigation with state)
            if (onPlayClick) {
                onPlayClick(puzzle);
            } else if (getNavigationPath) {
                // Fallback to direct navigation
                const path = getNavigationPath(puzzle);
                if (path) {
                    navigate(path);
                }
            }
        }
    };

    const handleReplayConfirm = async () => {
        setReplayConfirmOpen(false);

        // Call restartPlay for Replay
        if (userPayPlan && service) {
            try {
                await service.restartPlay(puzzle.id);
                Logger.log('Puzzle play restarted successfully');
            } catch (error) {
                Logger.error('Error restarting puzzle play:', error);
            }
        }

        // Navigate to puzzle play
        if (onPlayClick) {
            onPlayClick(puzzle);
        } else if (getNavigationPath) {
            const path = getNavigationPath(puzzle);
            if (path) {
                navigate(path);
            }
        }
    };

    const handleUpgradeClick = () => {
        setSubscriptionModalOpen(true);
    };

    const handleLockedButtonClick = async () => {
        if (isAnonymous) {
            try {
                await AuthService.signup();
            } catch (error) {
                Logger.error("Error redirecting to signup:", error);
            }
            return;
        }
        handleUpgradeClick();
    };

    const handleSubscriptionClose = () => {
        setSubscriptionModalOpen(false);
    };

    const handleSubscriptionSuccess = (plan) => {
        Logger.log('Subscription successful:', plan);
        setSubscriptionModalOpen(false);
        window.location.reload();
    };

    const isNew = isNewPuzzle();
    const showFreeTierBadge = !UtilService.isStandardPayPlan(userPayPlan);
    const showStandardTierBadge = !hideTierBadges && !markedFree
        && (puzzleTypeUpper === "SLIDING" || showFreeTierBadge);
    const showFreeChip = !hideTierBadges && markedFree && !hideFreeTierBadge;
    /** Chips absolutely positioned above the Card (`top: -9` + offset) so they sit just outside the card; wrapper stays `overflow: visible`. */
    const hasTopBadges = isNew || showStandardTierBadge || showFreeChip;

    const cardContentPtSx = (theme) =>
        theme.spacing(
            browseStoriesChrome ? Math.max(1, cardContentPadding - 2) : cardContentPadding,
        );

    /** Shown beside difficulty chips, above the mini chart (not below). */
    let publishedMetaNode = null;
    if (resolvedPublishedAt && !playComplete) {
        const pubMs = new Date(resolvedPublishedAt).getTime();
        if (Number.isFinite(pubMs) && pubMs > 0) {
            const publishedAgo = UtilService.formatHumanizeDetailed(Math.abs(Date.now() - pubMs), lang);
            if (publishedAgo) {
                publishedMetaNode = (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, textAlign: "right" }}>
                        {t("puzzle.card.published")}: {publishedAgo}
                    </Typography>
                );
            }
        }
    }

    const showPublishedInDifficultyRow = publishedMetaNode != null && !browseStoriesChrome;
    const jigsawTileSize =
        puzzle.type === "JIGSAW"
            ? Number(puzzle.tileSize ?? resolveJigsawTemplateTileSize(puzzle)) || 0
            : 0;
    const jigsawLevelInfo =
        jigsawTileSize > 0
            ? getJigsawDifficultyDisplayInfo(jigsawTileSize, puzzle.difficultyLevel)
            : null;
    const showDifficultyMetaRow =
        puzzle.difficultyLevel ||
        jigsawLevelInfo ||
        !hideWordCountChip ||
        showPublishedInDifficultyRow;

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    minHeight: 0,
                    overflow: 'visible',
                }}
            >
                {hasTopBadges && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -9 + (Number.isFinite(Number(topBadgesOffsetY)) ? Number(topBadgesOffsetY) : 0),
                            left: 8,
                            right: 8,
                            zIndex: 2,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            alignItems: 'center',
                            alignContent: 'flex-start',
                            pointerEvents: 'none',
                            overflow: 'visible',
                        }}
                    >
                        {isNew && (
                            <Tooltip title={t("puzzle.card.published-in-days")}>
                                <Chip
                                    icon={<NewReleases />}
                                    label={t("puzzle.card.new")}
                                    size="small"
                                    color="error"
                                    variant="filled"
                                    sx={{
                                        ...tierBadgeChipSx,
                                        backgroundColor: "#ff4444",
                                        color: "white",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                                        "& .MuiChip-label": {
                                            ...tierBadgeChipSx["& .MuiChip-label"],
                                            fontWeight: 700,
                                        },
                                    }}
                                />
                            </Tooltip>
                        )}
                        {showFreeChip && (
                                <Tooltip title={t("puzzle.card.free-puzzle")}>
                                    <Chip
                                        icon={<LockOpen />}
                                        label={t("puzzle.card.free")}
                                        size="small"
                                        color="success"
                                        variant="filled"
                                        sx={{
                                            ...tierBadgeChipSx,
                                        }}
                                    />
                                </Tooltip>
                            )}
                        {showStandardTierBadge && (
                                <Tooltip title={t("puzzle.card.premium-puzzle")}>
                                    <Chip
                                        icon={<StarIcon />}
                                        label={t("puzzle.card.premium")}
                                        size="small"
                                        color="warning"
                                        variant="filled"
                                        sx={tierBadgeChipSx}
                                    />
                                </Tooltip>
                            )}
                    </Box>
                )}

                <Card
                variant={browseStoriesChrome ? "outlined" : undefined}
                sx={{
                    ...(browseStoriesChrome
                        ? {
                              backgroundColor: "background.paper",
                              borderColor: "divider",
                          }
                        : {
                              backgroundColor: isLocked ? SURFACE.sunken : SURFACE.card,
                          }),
                    opacity: browseStoriesChrome ? 1 : isLocked ? 0.7 : 1,
                    position: "relative",
                    width: "100%",
                    maxWidth: "100%",
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    ...(browseStoriesChrome
                        ? {
                              transition: "box-shadow 0.2s, border-color 0.2s",
                              overflow: "visible",
                              "&:hover": {
                                  transform: "none",
                                  boxShadow: isLocked ? 1 : 3,
                                  borderColor: isLocked ? "divider" : "primary.light",
                              },
                          }
                        : {
                              transition: "all 0.2s ease-in-out",
                              overflow: "hidden",
                              "&:hover": {
                                  transform: isLocked ? "none" : "translateY(-2px)",
                                  boxShadow: isLocked ? 1 : 4,
                              },
                          }),
                }}
            >
                {/* Genre Gradient Band */}
                {!browseStoriesChrome ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: ColorService.getGenreGradient(puzzle.genre),
                            zIndex: 1,
                        }}
                    />
                ) : null}

                <CardContent
                    sx={{
                        pt: cardContentPtSx,
                        flex: '1 1 0%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: browseStoriesChrome ? 'visible' : 'hidden',
                        ...(browseStoriesChrome ? { pb: 1.5 } : {}),
                    }}
                >
                    {/* Title — hidden for SLIDING cards (shown in the story teaser slot below) */}
                    {puzzle.type !== 'SLIDING' && (
                    <Typography
                        variant="subtitle1"
                        gutterBottom={!browseStoriesChrome}
                        sx={{
                            fontWeight: 600,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '1.75em',
                            ...(browseStoriesChrome
                                ? { mb: 0.5 }
                                : { minHeight: '3.5em' }),
                            ...(isDisabled && { color: 'grey.600' })
                        }}
                    >
                        {puzzle.title}
                    </Typography>
                    )}

                    {showDifficultyMetaRow && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                flexWrap: "wrap",
                                flexShrink: 0,
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    minWidth: 0,
                                }}
                            >
                                {/* Difficulty chip — hidden for SLIDING (rendered below image in BrowseStylePuzzleCard) */}
                                {puzzle.type === "JIGSAW" && jigsawLevelInfo ? (
                                    <Chip
                                        label={t(jigsawLevelInfo.levelKey)}
                                        size="small"
                                        sx={{
                                            backgroundColor:
                                                jigsawLevelInfo.chipSx?.bgcolor ??
                                                ColorService.getDifficultyColor(jigsawLevelInfo.level),
                                            color: jigsawLevelInfo.chipSx?.color ?? "white",
                                            fontWeight: jigsawLevelInfo.chipSx?.["& .MuiChip-label"]?.fontWeight,
                                            ...jigsawLevelInfo.chipSx,
                                        }}
                                    />
                                ) : puzzle.difficultyLevel && puzzle.type !== "SLIDING" ? (
                                    <Chip
                                        label={
                                            puzzle.difficultyLevel === "EASY"
                                                ? t("puzzle.card.difficulty-easy")
                                                : puzzle.difficultyLevel === "MEDIUM"
                                                  ? t("puzzle.card.difficulty-medium")
                                                  : puzzle.difficultyLevel === "HARD"
                                                    ? t("puzzle.card.difficulty-hard")
                                                    : puzzle.difficultyLevel === "PRO"
                                                      ? t("puzzle.jigsaw.level-pro")
                                                      : puzzle.difficultyLevel === "ELITE"
                                                        ? t("puzzle.jigsaw.level-elite")
                                                        : puzzle.difficultyLevel === "INSANE"
                                                          ? t("puzzle.jigsaw.level-insane")
                                                          : puzzle.difficultyLevel
                                        }
                                        size="small"
                                        sx={{
                                            backgroundColor: ColorService.getDifficultyColor(puzzle.difficultyLevel),
                                            color: "white",
                                            textTransform: "capitalize",
                                        }}
                                    />
                                ) : null}
                                {!hideWordCountChip && (
                                    <Chip
                                        label={t("puzzle.card.words-count", { count: wordCount })}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                            {showPublishedInDifficultyRow ? publishedMetaNode : null}
                        </Box>
                    )}

                    {belowDifficultySlot ? (
                        <Box
                            sx={{
                                width: "100%",
                                flexShrink: 0,
                                mt: browseStoriesChrome ? 0.5 : 1,
                                minWidth: browseStoriesChrome ? "auto" : 0,
                                overflow: browseStoriesChrome ? "visible" : undefined,
                            }}
                        >
                            {belowDifficultySlot}
                        </Box>
                    ) : null}

                    {showClusteredBubblesMiniChart ? (
                        <Box
                            sx={{
                                width: "100%",
                                mt: browseStoriesChrome ? 0 : 0.25,
                                flex: '1 1 0%',
                                minHeight: 88,
                                maxHeight: '100%',
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                overflow: 'hidden',
                            }}
                        >
                            <ClusteredBubblesMiniChart
                                fillAvailableHeight
                                clusteredBubbles={puzzle.template.clusteredBubbles}
                            />
                        </Box>
                    ) : null}

                    {playData && playComplete && (() => {
                        const completionTime = UtilService.formatCompletionTime(playData.startedAt, playData.finishedAt);
                        const completedAt = playData.finishedAt || playData.updatedAt;
                        return (
                            <>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {t("puzzle.card.completed")}: {UtilService.formatRelativeTime(completedAt)}
                                </Typography>
                                {completionTime && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {t("puzzle.card.time-taken")}: {completionTime}
                                    </Typography>
                                )}
                            </>
                        );
                    })()}

                    {/* Disabled Message */}
                    {isDisabled && (() => {
                        const disabledAt = puzzle.disabledAt ?? puzzle.template?.disabledAt;
                        const disabledDateStr = disabledAt ? moment(disabledAt).format("MMM D, YYYY") : null;
                        const removalAt = disabledAt ? disabledAt + 7 * 24 * 60 * 60 * 1000 : null;
                        const diffMs = removalAt ? removalAt - Date.now() : 0;
                        const days = diffMs > 0 ? Math.floor(diffMs / (24 * 60 * 60 * 1000)) : 0;
                        const hours = diffMs > 0 ? Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)) : 0;
                        const removalText = diffMs <= 0
                            ? t("puzzle.card.scheduled-removal-soon")
                            : days > 0
                                ? (hours > 0
                                    ? t("puzzle.card.scheduled-removal-in-combo", {
                                        days,
                                        daysUnit: days === 1 ? t("puzzle.card.day") : t("puzzle.card.days"),
                                        hours,
                                        hoursUnit: hours === 1 ? t("puzzle.card.hour") : t("puzzle.card.hours")
                                    })
                                    : t("puzzle.card.scheduled-removal-in", {
                                        count: days,
                                        unit: days === 1 ? t("puzzle.card.day") : t("puzzle.card.days")
                                    }))
                                : t("puzzle.card.scheduled-removal-in", {
                                    count: hours,
                                    unit: hours === 1 ? t("puzzle.card.hour") : t("puzzle.card.hours")
                                });
                        return (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                {t("puzzle.card.disabled-msg")}
                                {disabledDateStr && (
                                    <>
                                        <br />
                                        {t("puzzle.card.disabled-on", { date: disabledDateStr })}
                                    </>
                                )}
                                {removalAt && (
                                    <>
                                        <br />
                                        {removalText}
                                    </>
                                )}
                            </Alert>
                        );
                    })()}

                    {/* When there is no bubble chart, absorb extra row height here so the Play row stays at the bottom */}
                    <Box
                        sx={{
                            flex: showClusteredBubblesMiniChart ? 0 : 1,
                            minHeight:
                                showClusteredBubblesMiniChart
                                    ? 0
                                    : browseStoriesChrome
                                      ? 4
                                      : 8,
                            minWidth: 0,
                        }}
                    />

                    {/* Progress + paused — above Play row */}
                    {progress !== null && (
                        <Box sx={{ width: "100%", mt: 0.25, mb: 0.5, flexShrink: 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t("puzzle.card.progress")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {progress}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: SURFACE.hover,
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 3,
                                        backgroundColor: "#3BA666",
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {playData && !playComplete && playData.updatedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "right", flexShrink: 0, mb: 0.25 }}>
                            {t("puzzle.card.paused")}: {UtilService.formatRelativeTime(playData.updatedAt)}
                        </Typography>
                    )}

                    {/* Play button and tags - always at bottom (browse: tags left, play right) */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: stackPrimaryAction ? 'column' : browseStoriesChrome ? 'row-reverse' : 'row',
                            alignItems: stackPrimaryAction ? 'stretch' : 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                            flexShrink: 0,
                            width: stackPrimaryAction ? '100%' : undefined,
                            pt: stackPrimaryAction ? 0.5 : browseStoriesChrome ? 0 : 0.5,
                            mt: 0,
                        }}
                    >
                        {isLocked ? (
                            isAnonymous ? (
                                <Tooltip title={t("puzzle.card.play-sign-in-tooltip")} arrow>
                                    <span>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            disabled
                                            startIcon={<Lock />}
                                        >
                                            {t("puzzle.card.play")}
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleLockedButtonClick}
                                    startIcon={
                                        isAnonymous ? <Lock /> : <StarIcon />
                                    }
                                >
                                    {isAnonymous
                                        ? t("puzzle.card.sign-in-to-play")
                                        : t("puzzle.card.upgrade-to-play")}
                                </Button>
                            )
                        ) : primaryActionReplacement != null ? (
                            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                                {primaryActionReplacement}
                            </Box>
                        ) : (
                            <Tooltip 
                                title={isDisabled && buttonText === "Replay" ? t("puzzle.card.puzzle-disabled-tooltip") : ""}
                                arrow
                            >
                                <span>
                                    <Button
                                        size="small"
                                        fullWidth={stackPrimaryAction}
                                        variant="contained"
                                        startIcon={stackPrimaryAction ? <VisibilityRounded /> : undefined}
                                        onClick={handlePlayClick}
                                        disabled={isDisabled && buttonText === "Replay"}
                                        sx={actionButtonSx || playContainedButtonSx}
                                    >
                                        {(buttonText === "Play" ? t("puzzle.card.play") : buttonText === "Replay" ? t("puzzle.card.replay") : buttonText)}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                flexWrap: 'wrap',
                                flex: 1,
                                minWidth: 0,
                                justifyContent: browseStoriesChrome ? 'flex-start' : 'flex-end',
                            }}
                        >
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            </Box>

            <SubscriptionDialog
                open={subscriptionModalOpen}
                onClose={handleSubscriptionClose}
                onSubscribe={handleSubscriptionSuccess}
                triggerReason="puzzle"
                userSubscription={isAnonymous ? 'free' : (UtilService.isStandardPayPlan(userPayPlan) ? 'premium' : 'free')}
            />

            {/* Replay Confirmation Dialog */}
            <Dialog
                open={replayConfirmOpen}
                onClose={() => setReplayConfirmOpen(false)}
                aria-labelledby="replay-dialog-title"
            >
                <DialogTitle id="replay-dialog-title">
                    {t("puzzle.card.play-again")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("puzzle.card.play-again-msg")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReplayConfirmOpen(false)} color="primary">
                        {t("puzzle.card.cancel")}
                    </Button>
                    <Button onClick={handleReplayConfirm} color="primary" variant="contained">
                        {t("puzzle.card.yes-play-again")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PuzzleCardBase;

